import { useState } from 'react';
import { Copy, Download, Check } from 'lucide-react';
import Editor from './Editor';

interface OutputPanelProps {
  outputs: {
    typescript: string;
    zod: string;
    openapi: string;
    toon: string;
    json: string;
    tokens: string;
  };
  darkMode: boolean;
}

type TabType = 'typescript' | 'zod' | 'openapi' | 'toon' | 'json' | 'tokens';

const TABS: { id: TabType; label: string; language: string; filename: string }[] = [
  { id: 'typescript', label: 'TypeScript', language: 'typescript', filename: 'interfaces.ts' },
  { id: 'zod', label: 'Zod', language: 'typescript', filename: 'schemas.ts' },
  { id: 'openapi', label: 'OpenAPI', language: 'json', filename: 'openapi.json' },
  { id: 'toon', label: 'TOON', language: 'plaintext', filename: 'data.toon' },
  { id: 'json', label: 'JSON', language: 'json', filename: 'data.json' },
  { id: 'tokens', label: 'Tokens', language: 'plaintext', filename: 'tokens.txt' },
];

function OutputPanel({ outputs, darkMode }: OutputPanelProps) {
  const [activeTab, setActiveTab] = useState<TabType>('typescript');
  const [copied, setCopied] = useState(false);

  const currentTab = TABS.find((t) => t.id === activeTab)!;
  const content = outputs[activeTab];

  const handleCopy = async () => {
    if (!content) return;
    await navigator.clipboard.writeText(content);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownload = () => {
    if (!content) return;
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = currentTab.filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="h-full flex flex-col">
      {/* Tabs */}
      <div className="px-6 py-4 border-b border-gray-200 dark:border-dark-border bg-white dark:bg-dark-surface">
        <div className="flex items-center justify-between">
          <div className="flex gap-2 overflow-x-auto">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-2 text-sm font-medium rounded-lg whitespace-nowrap transition-all ${
                  activeTab === tab.id
                    ? 'bg-[#1A535C] text-white shadow-lg shadow-[#1A535C]/20'
                    : 'bg-gray-100 dark:bg-dark-bg text-gray-600 dark:text-dark-muted hover:bg-gray-200 dark:hover:bg-dark-border'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {content && (
            <div className="flex gap-2 ml-4">
              <button
                onClick={handleCopy}
                className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-dark-bg text-gray-600 dark:text-dark-muted hover:text-[#FF6B6B] transition-colors"
                title="Copy to clipboard"
              >
                {copied ? <Check className="w-4 h-4 text-[#4ECDC4]" /> : <Copy className="w-4 h-4" />}
              </button>
              <button
                onClick={handleDownload}
                className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-dark-bg text-gray-600 dark:text-dark-muted hover:text-[#FF6B6B] transition-colors"
                title="Download file"
              >
                <Download className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1">
        {content ? (
          <Editor
            value={content}
            onChange={() => {}}
            language={currentTab.language}
            darkMode={darkMode}
            readOnly
          />
        ) : (
          <div className="h-full flex items-center justify-center text-gray-400 dark:text-dark-muted">
            {activeTab === 'tokens' ? 'Enter JSON or TOON to see token comparison' : 'Enter valid input to generate output'}
          </div>
        )}
      </div>
    </div>
  );
}

export default OutputPanel;
